import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import {
  Wallet,
  Plus,
  Trash,
  CircleNotch,
  CheckCircle,
  Clock,
  PencilSimple,
} from "@phosphor-icons/react";
import Pagination from "../shared/Pagination";
import { useAutoRefresh } from "../../hooks/useAutoRefresh";

const API = `${window.location.origin}/api`;

const MONTHS_PL = [
  "Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec",
  "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień",
];

const fmtPLN = (v) =>
  Number(v || 0).toLocaleString("pl-PL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

function getTargetOptions(refDate) {
  if (!refDate) return [];
  const [y, m] = refDate.split("-").map(Number);
  const prevMonth = m === 1 ? 12 : m - 1;
  const prevYear = m === 1 ? y - 1 : y;
  const nextMonth = m === 12 ? 1 : m + 1;
  const nextYear = m === 12 ? y + 1 : y;
  return [
    { year: prevYear, month: prevMonth, period: "01-15", label: `${MONTHS_PL[prevMonth - 1]} ${prevYear} — 01-15` },
    { year: prevYear, month: prevMonth, period: "16-30", label: `${MONTHS_PL[prevMonth - 1]} ${prevYear} — 16-30` },
    { year: y, month: m, period: "01-15", label: `${MONTHS_PL[m - 1]} ${y} — 01-15` },
    { year: y, month: m, period: "16-30", label: `${MONTHS_PL[m - 1]} ${y} — 16-30` },
    { year: nextYear, month: nextMonth, period: "01-15", label: `${MONTHS_PL[nextMonth - 1]} ${nextYear} — 01-15` },
    { year: nextYear, month: nextMonth, period: "16-30", label: `${MONTHS_PL[nextMonth - 1]} ${nextYear} — 16-30` },
  ];
}

export default function EmployeePozyczkaTab({ user }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ data: today, kwota: "", note: "" });
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showTargetPicker, setShowTargetPicker] = useState(false);
  const targetOptions = getTargetOptions(form.data);
  const [targetIdx, setTargetIdx] = useState(4); // default: next month 01-15
  const [editingDraft, setEditingDraft] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const paginated = useMemo(() => {
    const s = (page - 1) * pageSize;
    return drafts.slice(s, s + pageSize);
  }, [drafts, page, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [drafts.length, pageSize]);

  const fetchDrafts = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/employee-pozyczka`);
      setDrafts(data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDrafts();
  }, [fetchDrafts]);

  // Refresh when admin approves/applies drafts.
  useAutoRefresh(fetchDrafts);

  const startSave = (e) => {
    e?.preventDefault();
    if (!form.data || !form.kwota || Number(form.kwota) <= 0) {
      alert("Podaj datę i kwotę (>0).");
      return;
    }
    setEditingDraft(null);
    setTargetIdx(4);
    setShowTargetPicker(true);
  };

  const startEdit = (draft) => {
    setEditingDraft(draft);
    setForm({ data: draft.data, kwota: String(draft.kwota), note: draft.note || "" });
    const opts = getTargetOptions(draft.data);
    const idx = opts.findIndex(
      (o) =>
        o.year === draft.target_year &&
        o.month === draft.target_month &&
        o.period === draft.target_period
    );
    setTargetIdx(idx >= 0 ? idx : 4);
    setShowTargetPicker(true);
  };

  const confirmSave = async () => {
    const target = targetOptions[targetIdx];
    if (!target) {
      alert("Nieprawidłowy okres docelowy.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        data: form.data,
        kwota: parseFloat(form.kwota),
        target_year: target.year,
        target_month: target.month,
        target_period: target.period,
        note: form.note || null,
      };
      if (editingDraft) {
        await axios.put(`${API}/employee-pozyczka/${editingDraft.id}`, payload);
      } else {
        await axios.post(`${API}/employee-pozyczka`, payload);
      }
      setForm({ data: today, kwota: "", note: "" });
      setEditingDraft(null);
      setShowTargetPicker(false);
      await fetchDrafts();
    } catch (err) {
      alert("Błąd: " + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const cancelTargetPicker = () => {
    if (saving) return;
    setShowTargetPicker(false);
    if (editingDraft) {
      setForm({ data: today, kwota: "", note: "" });
      setEditingDraft(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Czy na pewno chcesz usunąć ten wpis?")) return;
    try {
      await axios.delete(`${API}/employee-pozyczka/${id}`);
      await fetchDrafts();
    } catch (err) {
      alert("Błąd: " + (err.response?.data?.detail || err.message));
    }
  };

  const total = useMemo(
    () => drafts.reduce((s, d) => s + (d.applied ? 0 : Number(d.kwota || 0)), 0),
    [drafts]
  );

  return (
    <div className="space-y-6" data-testid="employee-pozyczka-tab">
      <div className="bg-muted text-white rounded-sm p-4 sm:p-6 shadow-lg">
        <div className="flex items-center gap-3 mb-3">
          <Wallet className="w-8 h-8" weight="duotone" />
          <h2 className="text-2xl font-bold">Pożyczki / Zaliczki</h2>
        </div>
        <p className="text-violet-50 text-sm">
          Dodaj prośbę o zaliczkę z wynagrodzenia. Administrator doliczy ją jako
          potrącenie w wybranym okresie wypłaty.
        </p>
        <div className="mt-4 text-3xl font-bold tabular-nums">
          {fmtPLN(total)} zł{" "}
          <span className="text-violet-100 text-base font-medium">
            oczekuje na rozliczenie
          </span>
        </div>
      </div>

      <form
        onSubmit={startSave}
        className="bg-white border border-slate-200 rounded-xl shadow-sm p-5"
      >
        <h3 className="font-semibold text-slate-800 mb-4">Nowa pożyczka</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1 block">
              Data
            </label>
            <input
              type="date"
              value={form.data}
              onChange={(e) => setForm({ ...form, data: e.target.value })}
              className="input-field w-full"
              data-testid="pozyczka-date-input"
              required
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1 block">
              Kwota (zł)
            </label>
            <input
              type="number"
              step="0.01"
              min="0.01"
              value={form.kwota}
              onChange={(e) => setForm({ ...form, kwota: e.target.value })}
              className="input-field w-full"
              placeholder="np. 500"
              data-testid="pozyczka-kwota-input"
              required
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1 block">
              Notatka (opcjonalnie)
            </label>
            <input
              type="text"
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              className="input-field w-full"
              placeholder="np. Zaliczka na remont"
              data-testid="pozyczka-note-input"
            />
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <button
            type="submit"
            className="px-5 py-2.5 bg-violet-600 hover:bg-violet-700 text-white rounded-lg font-semibold flex items-center gap-2"
            data-testid="pozyczka-save-btn"
          >
            <Plus className="w-4 h-4" /> Dodaj pożyczkę
          </button>
        </div>
      </form>

      {showTargetPicker && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
          data-testid="pozyczka-target-modal"
          onClick={cancelTargetPicker}
        >
          <div
            className="bg-white rounded-xl border border-border max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-bold text-lg text-slate-800 mb-3">
              {editingDraft ? "Edytuj pożyczkę" : "Do której wypłaty doliczyć?"}
            </h3>
            <p className="text-sm text-slate-600 mb-4">
              Wpis: <b>{form.data}</b> — <b>{fmtPLN(form.kwota)} zł</b>.
              Wybierz okres wypłaty (poprzedni / bieżący / następny miesiąc).
            </p>
            <div className="space-y-2">
              {targetOptions.map((opt, i) => (
                <label
                  key={i}
                  className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                    targetIdx === i
                      ? "border-violet-500 bg-violet-50"
                      : "border-slate-200 hover:bg-slate-50"
                  }`}
                  data-testid={`pozyczka-target-option-${i}`}
                >
                  <input
                    type="radio"
                    checked={targetIdx === i}
                    onChange={() => setTargetIdx(i)}
                    className="w-4 h-4"
                  />
                  <span className="font-medium text-slate-700">{opt.label}</span>
                </label>
              ))}
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={cancelTargetPicker}
                disabled={saving}
                className="flex-1 px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50"
              >
                Anuluj
              </button>
              <button
                onClick={confirmSave}
                disabled={saving}
                className="flex-1 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="pozyczka-confirm-target-btn"
              >
                {saving ? (
                  <>
                    <CircleNotch className="w-4 h-4 animate-spin" /> Zapisuję...
                  </>
                ) : editingDraft ? (
                  "Zapisz zmiany"
                ) : (
                  "Zapisz"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200">
          <h3 className="font-semibold text-slate-800">Moje pożyczki</h3>
        </div>
        {loading ? (
          <div className="py-12 text-center text-slate-400">Ładowanie...</div>
        ) : drafts.length === 0 ? (
          <div className="py-12 text-center text-slate-500">
            Brak wpisów. Dodaj pierwszą pożyczkę powyżej.
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[600px]">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left py-3 px-4 text-xs font-bold text-slate-600 uppercase tracking-wider">Data</th>
                    <th className="text-right py-3 px-4 text-xs font-bold text-slate-600 uppercase tracking-wider">Kwota</th>
                    <th className="text-center py-3 px-4 text-xs font-bold text-slate-600 uppercase tracking-wider">Rozliczone</th>
                    <th className="text-left py-3 px-4 text-xs font-bold text-slate-600 uppercase tracking-wider">Wypłata</th>
                    <th className="text-left py-3 px-4 text-xs font-bold text-slate-600 uppercase tracking-wider">Notatka</th>
                    <th className="text-center py-3 px-4 text-xs font-bold text-slate-600 uppercase tracking-wider">Status</th>
                    <th className="text-right py-3 px-4 text-xs font-bold text-slate-600 uppercase tracking-wider">Akcje</th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.map((d) => (
                    <tr
                      key={d.id}
                      className="border-t border-slate-100 hover:bg-slate-50"
                      data-testid={`pozyczka-row-${d.id}`}
                    >
                      <td className="py-3 px-4 font-medium text-slate-800">{d.data}</td>
                      <td className="py-3 px-4 text-right tabular-nums font-bold text-slate-800">
                        {fmtPLN(d.kwota)} zł
                      </td>
                      <td className="py-3 px-4 text-center" data-testid={`pozyczka-paid-${d.id}`}>
                        {d.applied ? (
                          <CheckCircle className="w-5 h-5 text-emerald-600 inline-block" weight="fill" />
                        ) : (
                          <span className="text-slate-300 text-xl">○</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-600">
                        {MONTHS_PL[(d.target_month || 1) - 1]} {d.target_year} — {d.target_period}
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-500">{d.note || "—"}</td>
                      <td className="py-3 px-4 text-center">
                        {d.applied ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">
                            <CheckCircle className="w-3.5 h-3.5" weight="fill" />
                            Zastosowano
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">
                            <Clock className="w-3.5 h-3.5" weight="fill" />
                            Oczekuje
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-right">
                        {!d.applied && (
                          <div className="inline-flex items-center gap-1">
                            <button
                              onClick={() => startEdit(d)}
                              className="p-2 hover:bg-muted text-foreground rounded-lg"
                              data-testid={`pozyczka-edit-${d.id}`}
                              title="Edytuj"
                            >
                              <PencilSimple className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(d.id)}
                              className="p-2 hover:bg-red-50 text-red-600 rounded-lg"
                              data-testid={`pozyczka-delete-${d.id}`}
                              title="Usuń"
                            >
                              <Trash className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              page={page}
              pageSize={pageSize}
              total={drafts.length}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              testId="employee-pozyczka-pagination"
            />
          </>
        )}
      </div>
    </div>
  );
}
